import yaml from 'js-yaml'
import { sanitizeCardDescription } from '../utils/sanitize.js'
import { descriptionToPath, groupAndMergeReadModels, toCamelCase, toPascalCase } from './utils.js'
import { DomainModelRole } from '../constants/cardTypes.js'

const buildNestedOpenApiProperties = (fields, relatedEntity, getOpenApiType, allEntitiesMap) => {
  const properties = {}

  fields.forEach((subField) => {
    const relatedEntityField = relatedEntity?.fields?.find((f) => f.name === subField.name)
    const camelName = toCamelCase(subField.name)

    if (subField.fields?.length > 0) {
      const subRelatedEntityId = subField.relatedEntityId || relatedEntityField?.relatedEntityId
      const subRelatedEntity = subRelatedEntityId
        ? allEntitiesMap?.get(subRelatedEntityId)
        : null
      const cardinality = subField.cardinality || relatedEntityField?.cardinality
      const objectSchema = {
        type: 'object',
        properties: buildNestedOpenApiProperties(subField.fields, subRelatedEntity, getOpenApiType, allEntitiesMap)
      }

      if (cardinality === 'one-to-many') {
        properties[camelName] = {
          type: 'array',
          items: objectSchema
        }
      } else {
        properties[camelName] = objectSchema
      }
    } else {
      properties[camelName] = {
        type: getOpenApiType(relatedEntityField?.dataType),
        ...(relatedEntityField?.exampleData?.[0] && { example: relatedEntityField.exampleData[0] })
      }
    }
  })

  return properties
}

const sortObjectByKeys = (obj) => {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj
  }

  return Object.keys(obj)
    .sort()
    .reduce((result, key) => {
      result[key] = obj[key]

      return result
    }, {})
}

/**
 * Convert workflow data to OpenAPI 3.0 YAML specification
 *
 * @param {Object} workflowData - Workflow data containing entities and domainEvents
 * @param {Object} boundedContext - The bounded context to generate the spec for
 * @param {Array} allSchemas - All schemas from the workflow (optional, for MCP usage)
 * @param {Object} cardTypesMap - Map of cardTypeId to cardType (optional, for MCP usage)
 * @returns {string} OpenAPI 3.0 YAML specification
 */
export const convertWorkflowToSwaggerYaml = (workflowData, boundedContext, allSchemas = null, cardTypesMap = null) => {
  const swaggerSpec = {
    openapi: '3.0.3',
    info: {
      title: `${boundedContext.name} API Specification`,
      description: boundedContext.description || `API documentation for the ${boundedContext.name} bounded context.`,
      version: '1.0.0'
    },
    servers: [
      {
        url: '/api/v1',
        description: 'Development Server'
      }
    ],
    tags: [],
    paths: {},
    components: {
      schemas: {
        Error: {
          type: 'object',
          properties: {
            message: { type: 'string', example: 'An error occurred.' }
          }
        }
      },
      parameters: {},
      responses: {
        BadRequest: {
          description: 'Bad Request: The server cannot or will not process the request due to something that is perceived to be a client error.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/Error' } } }
        },
        NotFound: {
          description: 'Not Found: The server can not find the requested resource.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/Error' } } }
        },
        InternalServerError: {
          description: 'Internal Server Error: The server has encountered a situation it doesn\'t know how to handle.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/Error' } } }
        }
      }
    }
  }

  // Use pre-filtered entities from workflowData.entities (filtered by bounded context)
  const allEntities = workflowData.entities || []
  const allEntitiesMap = new Map(allEntities.map((entity) => [entity.id, entity]))

  // Helper to get domain model role from card
  // When cardTypesMap is provided (MCP), use it directly
  // When not provided (frontend), use card.cardType.domainModelRole
  const getRole = (card) => {
    if (cardTypesMap) {
      return cardTypesMap[card.cardTypeId]?.domainModelRole || null
    }

    return card.cardType?.domainModelRole || null
  }

  const contextEvents = (workflowData.domainEvents || []).filter((event) => {
    if (!event.cards) {
      return false
    }
    const entityCard = event.cards.find((r) => getRole(r) === DomainModelRole.AGGREGATE_ROOT)
    if (!entityCard) {
      return false
    }

    if (entityCard.schemaId && allEntitiesMap.has(entityCard.schemaId)) {
      return true
    }

    return !entityCard.schemaId && entityCard.boundedContext === boundedContext.id
  })

  const commands = []
  const readModels = []

  // Use allSchemas if provided (MCP), otherwise use workflowData.schemas (frontend)
  const schemasSource = allSchemas || workflowData.schemas || []

  contextEvents.forEach((event) => {
    if (!event.cards) {
      return
    }
    const aggregateCard = event.cards.find((r) => getRole(r) === DomainModelRole.AGGREGATE_ROOT)
    const associatedEntity = aggregateCard ? allEntitiesMap.get(aggregateCard.schemaId) : null

    event.cards.forEach((card) => {
      const schemaForReq = card.schemaId
        ? schemasSource.find((s) => s.id === card.schemaId)
        : null
      const fields = (schemaForReq?.fields || []).map((f) => ({
        name: f.name,
        type: f.type,
        relatedEntityId: f.relatedEntityId,
        cardinality: f.cardinality,
        fields: f.fields,
        dataType: f.dataType,
        tags: f.tags
      }))

      if (getRole(card) === DomainModelRole.COMMAND) {
        commands.push({ ...card, fields, associatedEntity })
      } else if (getRole(card) === DomainModelRole.READ_MODEL) {
        const readModelAssociatedEntity = schemaForReq?.entityId && allEntitiesMap.has(schemaForReq.entityId)
          ? allEntitiesMap.get(schemaForReq.entityId)
          : null

        if (readModelAssociatedEntity) {
          readModels.push({ ...card, fields, associatedEntity: readModelAssociatedEntity })
        }
      }
    })
  })

  const mergedReadModels = groupAndMergeReadModels(readModels, Array.from(allEntitiesMap.values()))

  const typeMap = {
    string: 'string',
    number: 'number',
    boolean: 'boolean',
    object: 'object'
  }

  const getOpenApiType = (dataType) => {
    return typeMap[dataType] || dataType || 'string'
  }

  for (const entity of allEntitiesMap.values()) {
    const entityNamePascal = toPascalCase(entity.name)
    const idField = entity.fields.find((f) => f.name.toLowerCase() === 'id')
    const entityData = {
      ...entity,
      namePascal: entityNamePascal,
      nameCamel: toCamelCase(entity.name),
      idField: idField
        ? {
            name: idField.name,
            nameCamel: toCamelCase(idField.name)
          }
        : null
    }

    const schemaName = entityData.namePascal
    const properties = {}
    const required = []

    entity.fields.forEach((field) => {
      const propertyName = toCamelCase(field.name)
      let property = {}

      if (field.relatedEntityId) {
        const relatedEntity = allEntitiesMap.get(field.relatedEntityId)
        const relatedEntityName = relatedEntity ? toPascalCase(relatedEntity.name) : field.name

        const isForeignKey = field.name.endsWith('Id')

        if (isForeignKey) {
          property = {
            type: 'string',
            description: `Reference to ${relatedEntity ? relatedEntity.name : 'related'} entity${relatedEntity ? ` (${relatedEntityName}.id)` : ''}.`,
            example: field.exampleData?.[0]
          }
        } else {
          if (field.cardinality === 'one-to-one') {
            property.$ref = `#/components/schemas/${relatedEntityName}`
          } else if (field.cardinality === 'one-to-many') {
            property.type = 'array'
            property.items = !relatedEntity ? { type: getOpenApiType(field.dataType), example: field.exampleData?.[0] } : { $ref: `#/components/schemas/${relatedEntityName}` }
          } else {
            property.$ref = `#/components/schemas/${relatedEntityName}`
          }
        }
      } else {
        property = {
          type: getOpenApiType(field.dataType),
          description: field.name.toLowerCase() === 'id'
            ? `Unique identifier for the ${entity.name}.`
            : `The ${field.name} of the ${entity.name}.`,
          example: field.exampleData?.[0]
        }
      }

      properties[propertyName] = property

      if (field.tags?.includes('REQUIRED')) {
        required.push(propertyName)
      }
    })

    swaggerSpec.components.schemas[schemaName] = {
      type: 'object',
      description: `Represents a ${entity.name} in the system.`,
      properties,
      ...(required.length > 0 && { required })
    }

    if (entityData.idField && entityData.idField.nameCamel) {
      swaggerSpec.components.parameters[`${schemaName}Id`] = {
        name: entityData.idField.nameCamel,
        in: 'path',
        required: true,
        description: `The unique identifier for the ${entity.name}.`,
        schema: { type: getOpenApiType(idField?.dataType) }
      }
    }
  }

  commands.forEach((command) => {
    if (!command.associatedEntity) {
      return
    }

    const entity = command.associatedEntity
    const cleanDescription = sanitizeCardDescription(command.description)
    const operationId = toCamelCase(cleanDescription)
    const tag = toPascalCase(boundedContext.name)

    if (tag && !swaggerSpec.tags.find((t) => t.name === tag)) {
      swaggerSpec.tags.push({ name: tag, description: `Operations related to the ${tag} bounded context.` })
    }

    const method = 'post'
    const path = descriptionToPath(cleanDescription)

    const operation = {
      summary: cleanDescription,
      description: `Executes the "${cleanDescription}" command.`,
      operationId,
      tags: [tag],
      responses: {
        202: { description: 'Accepted: The command has been accepted for processing.' },
        400: { $ref: '#/components/responses/BadRequest' }
      }
    }

    if (entity.idField) {
      const entityIdFieldName = entity.idField.name
      const commandHasIdField = command.fields.some((field) => field.name === entityIdFieldName)

      if (commandHasIdField) {
        operation.responses['404'] = { $ref: '#/components/responses/NotFound' }
      }
    }

    const requestFields = command.fields

    if (requestFields.length > 0 && (method === 'post' || method === 'put')) {
      const requestSchemaName = `${toPascalCase(cleanDescription)}Request`
      const properties = {}
      const required = []

      requestFields.forEach((field) => {
        const fieldCamelCase = toCamelCase(field.name)
        const entityField = entity.fields.find((f) => f.name === field.name)
        const hasNestedFields = field.fields?.length > 0

        if (hasNestedFields) {
          const relatedEntity = entityField?.relatedEntityId
            ? allEntitiesMap.get(entityField.relatedEntityId)
            : [...allEntitiesMap.values()].find((e) => toCamelCase(e.name) === field.name)
          const relatedEntityName = relatedEntity ? toPascalCase(relatedEntity.name) : toPascalCase(field.name)
          const inputSchemaName = `${relatedEntityName}Input`

          if (!swaggerSpec.components.schemas[inputSchemaName]) {
            const inputProperties = {}
            const inputRequired = []
            field.fields.forEach((subField) => {
              const subFieldCamel = toCamelCase(subField.name)
              const relatedEntityField = relatedEntity?.fields?.find((f) => f.name === subField.name)
              inputProperties[subFieldCamel] = {
                type: getOpenApiType(relatedEntityField?.dataType),
                ...(relatedEntityField?.exampleData?.[0] && { example: relatedEntityField.exampleData[0] })
              }
              if (relatedEntityField?.tags?.includes('REQUIRED')) {
                inputRequired.push(subFieldCamel)
              }
            })
            swaggerSpec.components.schemas[inputSchemaName] = {
              type: 'object',
              properties: inputProperties,
              ...(inputRequired.length > 0 && { required: inputRequired })
            }
          }

          if ((field.cardinality || entityField?.cardinality) === 'one-to-many') {
            properties[fieldCamelCase] = {
              type: 'array',
              items: { $ref: `#/components/schemas/${inputSchemaName}` }
            }
          } else {
            properties[fieldCamelCase] = { $ref: `#/components/schemas/${inputSchemaName}` }
          }
        } else if (entityField) {
          if (entityField.relatedEntityId || field.relatedEntityId) {
            const relatedEntity = allEntitiesMap.get(entityField.relatedEntityId || field.relatedEntityId)
            const relatedEntityName = relatedEntity ? toPascalCase(relatedEntity.name) : field.name
            properties[fieldCamelCase] = {
              type: 'string',
              description: `Reference to ${relatedEntity ? relatedEntity.name : 'related'} entity (${relatedEntityName}.id).`,
              example: entityField.exampleData?.[0]
            }
          } else {
            properties[fieldCamelCase] = {
              type: getOpenApiType(entityField.dataType),
              example: entityField.exampleData?.[0]
            }
          }
        } else {
          let resolvedType = null
          for (const e of allEntitiesMap.values()) {
            const match = e.fields.find((f) => f.name === field.name)
            if (match) {
              resolvedType = match.dataType
              break
            }
          }
          properties[fieldCamelCase] = { type: getOpenApiType(resolvedType) }
        }

        if (field.tags?.includes('REQUIRED')) {
          required.push(fieldCamelCase)
        }
      })

      swaggerSpec.components.schemas[requestSchemaName] = {
        type: 'object',
        properties,
        ...(required.length > 0 && { required })
      }

      operation.requestBody = {
        required: true,
        content: {
          'application/json': {
            schema: { $ref: `#/components/schemas/${requestSchemaName}` }
          }
        }
      }

      if (method === 'post') {
        operation.responses['200'] = {
          description: 'OK: The resource was updated successfully.',
          content: { 'application/json': { schema: { $ref: `#/components/schemas/${toPascalCase(entity.name)}` } } }
        }
        delete operation.responses['201']
        delete operation.responses['202']
      }
    }

    if (method === 'delete') {
      operation.responses['204'] = { description: 'No Content: The resource was deleted successfully.' }
      delete operation.responses['202']
    }

    if (!swaggerSpec.paths[path]) {
      swaggerSpec.paths[path] = {}
    }
    swaggerSpec.paths[path][method] = operation
  })

  mergedReadModels.forEach((readModel) => {
    if (!readModel.associatedEntity) {
      return
    }

    const entity = readModel.associatedEntity
    const cleanDescription = sanitizeCardDescription(readModel.description)
    const operationId = toCamelCase(cleanDescription)
    const tag = toPascalCase(boundedContext.name)
    const cardinality = readModel.cardinality

    if (tag && !swaggerSpec.tags.find((t) => t.name === tag)) {
      swaggerSpec.tags.push({ name: tag, description: `Operations related to the ${tag} bounded context.` })
    }

    const method = 'get'
    let path = descriptionToPath(cleanDescription)
    let parameters = []
    const isGetById = cardinality === 'one-to-one'
    const manualFilterFields = readModel.fields.filter((f) => f.type === 'filter')
    const entityIdField = entity.idField

    parameters = manualFilterFields.map((field) => {
      const correspondingEntityField = entity.fields.find((ef) => ef.name === field.name)
      const param = {
        in: 'path',
        name: toCamelCase(field.name),
        required: true,
        schema: { type: getOpenApiType(correspondingEntityField?.dataType) }
      }

      if (entityIdField && field.name === entityIdField.name) {
        param.description = `The unique identifier for the ${entity.name}.`
      }

      return param
    })

    if (isGetById && entityIdField) {
      const pkAlreadyAdded = parameters.some((p) => p.name === entityIdField.nameCamel)
      if (!pkAlreadyAdded) {
        const idFieldDef = entity.fields.find((f) => f.name.toLowerCase() === 'id')
        parameters.unshift({
          in: 'path',
          name: entityIdField.nameCamel,
          required: true,
          schema: { type: getOpenApiType(idFieldDef?.dataType) },
          description: `The unique identifier for the ${entity.name}.`
        })
      }
    }

    path += parameters.map((p) => `/{${p.name}}`).join('')

    const operation = {
      summary: cleanDescription,
      description: `Retrieves data based on the "${cleanDescription}" query. This is a synchronous query operation.`,
      operationId,
      tags: [tag],
      responses: {
        400: { $ref: '#/components/responses/BadRequest' }
      }
    }

    if (isGetById) {
      operation.responses['404'] = { $ref: '#/components/responses/NotFound' }
    }

    if (parameters.length > 0) {
      operation.parameters = parameters
    }

    let responseSchemaRef
    const readModelFields = new Set(readModel.fields.map((f) => f.name))
    const entityFields = new Set(entity.fields.map((f) => f.name))
    const fieldsMatch = readModelFields.size === entityFields.size && [...readModelFields].every((f) => entityFields.has(f))

    if (fieldsMatch && allEntitiesMap.has(entity.id)) {
      responseSchemaRef = { $ref: `#/components/schemas/${toPascalCase(entity.name)}` }
    } else {
      const responseSchemaName = `${toPascalCase(cleanDescription)}Response`
      const properties = {}

      readModel.fields.forEach((field) => {
        const fieldCamelCase = toCamelCase(field.name)
        const correspondingEntityField = entity.fields.find((ef) => ef.name === field.name)
        const hasNestedFields = field.fields?.length > 0

        if (hasNestedFields) {
          const relatedEntity = correspondingEntityField?.relatedEntityId
            ? allEntitiesMap.get(correspondingEntityField.relatedEntityId)
            : [...allEntitiesMap.values()].find((e) => toCamelCase(e.name) === field.name)
          const relatedEntityName = relatedEntity ? toPascalCase(relatedEntity.name) : toPascalCase(field.name)

          if (relatedEntity && swaggerSpec.components.schemas[relatedEntityName]) {
            if ((field.cardinality || correspondingEntityField?.cardinality) === 'one-to-many') {
              properties[fieldCamelCase] = {
                type: 'array',
                items: { $ref: `#/components/schemas/${relatedEntityName}` }
              }
            } else {
              properties[fieldCamelCase] = { $ref: `#/components/schemas/${relatedEntityName}` }
            }
          } else {
            properties[fieldCamelCase] = {
              type: 'object',
              properties: buildNestedOpenApiProperties(field.fields, relatedEntity, getOpenApiType, allEntitiesMap)
            }
          }
        } else if (field.relatedEntityId || correspondingEntityField?.relatedEntityId) {
          const relatedEntity = allEntitiesMap.get(field.relatedEntityId || correspondingEntityField.relatedEntityId)
          properties[fieldCamelCase] = {
            type: 'string',
            description: `Reference to ${relatedEntity ? relatedEntity.name : 'related'} entity.`,
            example: correspondingEntityField?.exampleData?.[0]
          }
        } else {
          properties[fieldCamelCase] = {
            type: getOpenApiType(correspondingEntityField?.dataType),
            description: correspondingEntityField?.description || `The ${field.name} of the ${entity.name}.`,
            example: correspondingEntityField?.exampleData?.[0]
          }
        }
      })

      swaggerSpec.components.schemas[responseSchemaName] = {
        type: 'object',
        description: `Response schema for the ${cleanDescription} query.`,
        properties
      }

      responseSchemaRef = { $ref: `#/components/schemas/${responseSchemaName}` }
    }

    const successResponse = {
      description: 'Successful response',
      content: { 'application/json': { schema: {} } }
    }

    if (isGetById) {
      successResponse.content['application/json'].schema = responseSchemaRef
    } else {
      successResponse.content['application/json'].schema = {
        type: 'array',
        items: responseSchemaRef
      }
    }
    operation.responses['200'] = successResponse

    if (!swaggerSpec.paths[path]) {
      swaggerSpec.paths[path] = {}
    }
    swaggerSpec.paths[path][method] = operation
  })

  // Sort schemas and parameters alphabetically by key
  swaggerSpec.paths = sortObjectByKeys(swaggerSpec.paths)
  swaggerSpec.components.schemas = sortObjectByKeys(swaggerSpec.components.schemas ?? {})
  swaggerSpec.components.parameters = sortObjectByKeys(swaggerSpec.components.parameters ?? {})
  swaggerSpec.components.responses = sortObjectByKeys(swaggerSpec.components.responses ?? {})

  return yaml.dump(swaggerSpec, { noRefs: false, lineWidth: -1 })
}
